import i18n from '@/utils/i18n';
import React, { useCallback, useMemo } from 'react';
import {
  buildOrderEntryMobileCardStats,
  OrderEntryMobileCardStatsContext,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/order-entry-mobile-card-stats';
import { FlashList } from '@shopify/flash-list';
import { LastPurchaseInfoMap } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';
import {
  ORDER_ENTRY_MOBILE_CARD_ESTIMATED_HEIGHT,
  OrderEntryMobileItemCard,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-item-card';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import {
  usePageLayoutBodyScroll,
  usePageLayoutHeaderInset,
  View,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileListProps {
  areSubstitutionsEnabled: boolean;
  bottomInset?: number;
  data: OrderEntryTableRowData[];
  emptyComponent: React.ReactElement;
  /** Visible configurable column ids in the rep's saved order. */
  fieldIds: string[];
  isGrossProfitHidden: boolean;
  isQuantityDisabled: boolean;
  labelsById: Record<string, string>;
  lastPurchaseInfoMap: LastPurchaseInfoMap;
  onOpenDetail: (variantPackUUID: string) => void;
  onQuantityChange: (variantPackUUID: string, quantity: number) => void;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  restaurantTimezone: string;
  selectedCollectionId: string;
  showConfidentialFields: boolean;
}

export const OrderEntryMobileList: React.FC<OrderEntryMobileListProps> = ({
  areSubstitutionsEnabled,
  bottomInset = 0,
  data,
  emptyComponent,
  fieldIds,
  isGrossProfitHidden,
  isQuantityDisabled,
  labelsById,
  lastPurchaseInfoMap,
  onOpenDetail,
  onQuantityChange,
  priceEditDisplayHeaders,
  restaurantTimezone,
  selectedCollectionId,
  showConfidentialFields,
}) => {
  const theme = useTheme();
  const { onScroll } = usePageLayoutBodyScroll();
  const headerInset = usePageLayoutHeaderInset();

  const statsContext = useMemo<OrderEntryMobileCardStatsContext>(
    () => ({
      areSubstitutionsEnabled,
      isGrossProfitHidden,
      labelsById,
      lastOrderLabel: i18n.t('sales_portal.order_entry.table.last_order'),
      lastPurchaseInfoMap,
      priceEditDisplayHeadersByKey: Object.fromEntries(
        priceEditDisplayHeaders.map((header) => [header.key, header]),
      ),
      restaurantTimezone,
      selectedCollectionId,
      showConfidentialFields,
    }),
    [
      areSubstitutionsEnabled,
      isGrossProfitHidden,
      labelsById,
      lastPurchaseInfoMap,
      priceEditDisplayHeaders,
      restaurantTimezone,
      selectedCollectionId,
      showConfidentialFields,
    ],
  );

  const renderItem = useCallback(
    ({ item }: { item: OrderEntryTableRowData }) => (
      <OrderEntryMobileItemCard
        imageUrl={item.imageUrl}
        isQuantityDisabled={isQuantityDisabled}
        labels={item.labels}
        maxQuantity={item.maxQuantity}
        minQuantity={item.minQuantity}
        name={item.name}
        onHandQuantity={areSubstitutionsEnabled ? item.onHandQuantity : null}
        onOpenDetail={onOpenDetail}
        onQuantityChange={onQuantityChange}
        stats={buildOrderEntryMobileCardStats(item, fieldIds, statsContext)}
        subtitle={`${item.packDisplayString} · ${item.externalItemId}`}
        variantPackUUID={item.variantPackUUID}
      />
    ),
    [
      areSubstitutionsEnabled,
      fieldIds,
      isQuantityDisabled,
      onOpenDetail,
      onQuantityChange,
      statsContext,
    ],
  );

  /** Changes with every row input, so FlashList re-renders the cards when the rep rearranges the fields. */
  const extraData = renderItem;

  const contentPadding = {
    paddingBottom: theme.sk.spacing.spaceSMedium + bottomInset,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingTop: headerInset,
  };

  // FlashList's ListEmptyComponent wrapper is content-sized, so a flex:1
  // empty state pins to the top; give it a real flex container instead.
  if (data.length === 0) {
    return (
      <YStack flex={1} {...contentPadding}>
        {emptyComponent}
      </YStack>
    );
  }

  return (
    <FlashList
      automaticallyAdjustKeyboardInsets
      contentContainerStyle={contentPadding}
      data={data}
      estimatedItemSize={ORDER_ENTRY_MOBILE_CARD_ESTIMATED_HEIGHT}
      extraData={extraData}
      ItemSeparatorComponent={ItemSeparator}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
      onScroll={onScroll}
      renderItem={renderItem}
      scrollEventThrottle={16}
    />
  );
};

const ItemSeparator: React.FC = () => {
  const theme = useTheme();
  return <View height={theme.sk.spacing.spaceSMedium} />;
};
